import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { useAuth, formatApiErrorDetail } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Separator } from "@/components/ui/separator";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  User as UserIcon,
  CreditCard,
  ShieldCheck,
  Bell,
  Gift,
  LifeBuoy,
  Sparkles,
  Loader2,
  CheckCircle2,
  Clock as ClockIcon,
  LogOut,
  BadgePercent,
  Plus,
  ArrowLeft,
  Download,
  Trash2,
  AlertTriangle,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
axios.defaults.withCredentials = true;

const COUNTRY_OPTIONS = [
  "India", "Pakistan", "Sri Lanka", "Bangladesh", "Nepal", "Philippines",
  "Iran", "Iraq", "Egypt", "United Kingdom", "United States", "Canada",
  "Australia", "New Zealand", "Other",
];

const LANGUAGE_OPTIONS = [
  "English", "Hindi", "Tamil", "Urdu", "Punjabi", "Bengali", "Tagalog",
  "Persian", "Arabic", "Spanish", "Mandarin", "Other",
];

const STUDY_GOALS = [
  { v: 30, label: "30 min/day" },
  { v: 60, label: "1 hour/day" },
  { v: 120, label: "2 hours/day" },
  { v: 180, label: "3+ hours/day" },
];

const formatDate = (iso) => {
  if (!iso) return "-";
  try {
    return new Date(iso).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
  } catch { return iso; }
};

const formatDateTime = (iso) => {
  if (!iso) return "-";
  try {
    return new Date(iso).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
  } catch { return iso; }
};

const daysUntil = (iso) => {
  if (!iso) return null;
  try {
    const diff = new Date(iso).getTime() - Date.now();
    return Math.max(0, Math.ceil(diff / (1000 * 60 * 60 * 24)));
  } catch { return null; }
};

const planBadge = (user) => {
  if (!user) return { label: "-", className: "bg-muted text-muted-foreground" };
  if (user.role === "admin") return { label: "Admin · Lifetime Pro", className: "bg-primary/15 text-primary border border-primary/30" };
  const paidUp = !!user.pro_until && new Date(user.pro_until) > new Date();
  if (user.subscription_status === "trialing") return { label: "Free Trial", className: "bg-accent/15 text-accent border border-accent/30" };
  if (user.cancel_at_period_end && paidUp) return { label: "Pro · Auto-renew off", className: "bg-warning/15 text-warning border border-warning/30" };
  if (user.subscription_status === "active") return { label: "Pro", className: "bg-primary/15 text-primary border border-primary/30" };
  return { label: "Free", className: "bg-muted text-foreground border border-border" };
};

const AccountPage = () => {
  const { user, refresh, isPro, logout } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [tab, setTab] = useState(searchParams.get("tab") || "profile");

  useEffect(() => {
    if (searchParams.get("tab") !== tab) {
      const np = new URLSearchParams(searchParams);
      np.set("tab", tab);
      setSearchParams(np, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  if (!user) return null;

  const badge = planBadge(user);
  const proDaysLeft = daysUntil(user.pro_until);

  return (
    <div className="min-h-screen bg-background pb-16">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 pt-10">
        {/* Header */}
        <div className="flex items-start gap-5 mb-8">
          <div className="w-16 h-16 rounded-2xl bg-primary/15 text-primary flex items-center justify-center text-2xl font-bold flex-shrink-0">
            {(user.name || user.email).charAt(0).toUpperCase()}
          </div>
          <div className="min-w-0">
            <h1 className="text-3xl sm:text-4xl font-bold tracking-tight">My Account</h1>
            <p className="text-muted-foreground mt-1 truncate">{user.email}</p>
            <div className="mt-3 flex flex-wrap items-center gap-2">
              <span data-testid="account-plan-badge" className={`text-xs font-semibold px-3 py-1 rounded-full ${badge.className}`}>
                {badge.label}
              </span>
              {proDaysLeft != null && isPro && user.role !== "admin" && (
                <span className="text-xs font-medium text-muted-foreground inline-flex items-center gap-1">
                  <ClockIcon className="w-3 h-3" /> {proDaysLeft} day{proDaysLeft === 1 ? "" : "s"} left
                </span>
              )}
            </div>
          </div>
        </div>

        <Tabs value={tab} onValueChange={setTab} className="w-full">
          <TabsList className="w-full justify-start overflow-x-auto h-auto p-1 bg-muted/60 mb-6 flex-wrap">
            <TabsTrigger value="profile" data-testid="tab-profile" className="gap-2">
              <UserIcon className="w-4 h-4" /> Profile
            </TabsTrigger>
            <TabsTrigger value="billing" data-testid="tab-billing" className="gap-2">
              <CreditCard className="w-4 h-4" /> Membership
            </TabsTrigger>
            <TabsTrigger value="refer" data-testid="tab-refer" className="gap-2">
              <Gift className="w-4 h-4" /> Refer & earn
            </TabsTrigger>
            <TabsTrigger value="support" data-testid="tab-support" className="gap-2">
              <LifeBuoy className="w-4 h-4" /> Support
            </TabsTrigger>
            <TabsTrigger value="security" data-testid="tab-security" className="gap-2">
              <ShieldCheck className="w-4 h-4" /> Security
            </TabsTrigger>
            <TabsTrigger value="notifications" data-testid="tab-notifications" className="gap-2">
              <Bell className="w-4 h-4" /> Notifications
            </TabsTrigger>
          </TabsList>

          <TabsContent value="profile"><ProfileTab user={user} refresh={refresh} /></TabsContent>
          <TabsContent value="billing"><BillingTab user={user} isPro={isPro} refresh={refresh} navigate={navigate} /></TabsContent>
          <TabsContent value="refer"><ReferTab /></TabsContent>
          <TabsContent value="support"><SupportTab /></TabsContent>
          <TabsContent value="security"><SecurityTab logout={logout} /></TabsContent>
          <TabsContent value="notifications"><NotificationsTab /></TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

// ============== PROFILE TAB ==============
const ProfileTab = ({ user, refresh }) => {
  const [form, setForm] = useState({
    name: user.name || "",
    exam_date: user.exam_date ? user.exam_date.slice(0, 10) : "",
    study_goal_minutes: user.study_goal_minutes || 60,
    country: user.country || "",
    native_language: user.native_language || "",
    university: user.university || "",
  });
  const [saving, setSaving] = useState(false);

  const onSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await axios.patch(`${API}/auth/profile`, form);
      await refresh();
      toast.success("Profile updated");
    } catch (err) {
      toast.error("Could not save profile", { description: formatApiErrorDetail(err?.response?.data?.detail) });
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={onSave} className="rounded-2xl border border-border bg-card p-6 sm:p-8 space-y-6" data-testid="profile-form">
      <div>
        <h2 className="text-lg font-semibold">Personal info</h2>
        <p className="text-sm text-muted-foreground">This helps tailor your study plan and tutor recommendations.</p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="name">Full name</Label>
          <Input id="name" data-testid="input-name" value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} placeholder="e.g. Dr Priya Kumar" />
        </div>
        <div className="space-y-2">
          <Label>Email</Label>
          <Input value={user.email} disabled />
          <p className="text-xs text-muted-foreground">Contact support to change your email.</p>
        </div>
        <div className="space-y-2">
          <Label htmlFor="country">Country</Label>
          <select id="country" data-testid="select-country" value={form.country} onChange={(e) => setForm((f) => ({ ...f, country: e.target.value }))} className="w-full h-10 rounded-md border border-input bg-background px-3 text-sm">
            <option value="">Select…</option>
            {COUNTRY_OPTIONS.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
        <div className="space-y-2">
          <Label htmlFor="native_language">Native language</Label>
          <select id="native_language" data-testid="select-language" value={form.native_language} onChange={(e) => setForm((f) => ({ ...f, native_language: e.target.value }))} className="w-full h-10 rounded-md border border-input bg-background px-3 text-sm">
            <option value="">Select…</option>
            {LANGUAGE_OPTIONS.map((l) => <option key={l} value={l}>{l}</option>)}
          </select>
        </div>
        <div className="space-y-2 sm:col-span-2">
          <Label htmlFor="university">Dental school / University (optional)</Label>
          <Input id="university" data-testid="input-university" value={form.university} onChange={(e) => setForm((f) => ({ ...f, university: e.target.value }))} placeholder="e.g. King George's Medical University" />
        </div>
      </div>

      <Separator />

      <div>
        <h2 className="text-lg font-semibold">Exam plan</h2>
        <p className="text-sm text-muted-foreground">Your study plan and dashboard countdown use these.</p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="exam_date">Exam date</Label>
          <Input id="exam_date" data-testid="input-exam-date" type="date" value={form.exam_date} onChange={(e) => setForm((f) => ({ ...f, exam_date: e.target.value }))} />
          <p className="text-xs text-muted-foreground">Leave blank if you haven't decided yet.</p>
        </div>
        <div className="space-y-2">
          <Label htmlFor="study_goal_minutes">Daily study goal</Label>
          <select id="study_goal_minutes" data-testid="select-goal" value={form.study_goal_minutes} onChange={(e) => setForm((f) => ({ ...f, study_goal_minutes: parseInt(e.target.value, 10) }))} className="w-full h-10 rounded-md border border-input bg-background px-3 text-sm">
            {STUDY_GOALS.map((g) => <option key={g.v} value={g.v}>{g.label}</option>)}
          </select>
        </div>
      </div>

      <div className="flex justify-end pt-2">
        <Button type="submit" disabled={saving} data-testid="save-profile-btn">
          {saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Saving…</> : "Save changes"}
        </Button>
      </div>
    </form>
  );
};

// ============== BILLING TAB ==============
const BillingTab = ({ user, isPro, refresh, navigate }) => {
  const [txns, setTxns] = useState([]);
  const [loadingTxns, setLoadingTxns] = useState(true);
  const [cancelling, setCancelling] = useState(false);

  useEffect(() => {
    axios.get(`${API}/billing/transactions`)
      .then(async (r) => {
        setTxns(r.data.transactions || []);
        // Server may have just pulled a missed payment from Stripe — reflect it now.
        if (r.data.reconciled?.reconciled) await refresh();
      })
      .catch(() => {})
      .finally(() => setLoadingTxns(false));
  }, [refresh]);

  const [syncing, setSyncing] = useState(false);
  const syncFromStripe = async () => {
    setSyncing(true);
    try {
      const r = await axios.post(`${API}/billing/reconcile`);
      await refresh();
      const t = await axios.get(`${API}/billing/transactions`);
      setTxns(t.data.transactions || []);
      if (r.data.reconciled) toast.success("Membership refreshed from Stripe", { description: `Status: ${r.data.status}` });
      else toast.info("No paid subscription found for this email on Stripe", { description: r.data.reason?.replace(/_/g, " ") });
    } catch (err) {
      toast.error("Could not refresh from Stripe", { description: formatApiErrorDetail(err?.response?.data?.detail) });
    } finally {
      setSyncing(false);
    }
  };

  const proDaysLeft = daysUntil(user.pro_until);
  const badge = planBadge(user);

  const onCancel = async () => {
    if (!window.confirm("Stop auto-renew? You'll keep Pro access until your current period ends.")) return;
    setCancelling(true);
    try {
      const r = await axios.post(`${API}/billing/cancel`);
      toast.success(r.data.message || "Auto-renew turned off.");
      await refresh();
    } catch (err) {
      toast.error("Could not cancel", { description: formatApiErrorDetail(err?.response?.data?.detail) });
    } finally {
      setCancelling(false);
    }
  };

  const onResume = async () => {
    setCancelling(true);
    try {
      const r = await axios.post(`${API}/billing/resume`);
      toast.success(r.data.message || "Auto-renew is back on.");
      await refresh();
    } catch (err) {
      toast.error("Could not resume", { description: formatApiErrorDetail(err?.response?.data?.detail) });
    } finally {
      setCancelling(false);
    }
  };

  const paidTxns = useMemo(() => txns.filter((t) => t.payment_status === "paid"), [txns]);

  return (
    <div className="space-y-6">
      {/* Current plan card */}
      <div className="rounded-2xl border border-border bg-card p-6 sm:p-8" data-testid="current-plan-card">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <p className="text-sm text-muted-foreground">Current plan</p>
            <div className="mt-1 flex items-center gap-3">
              <span className={`text-sm font-semibold px-3 py-1 rounded-full ${badge.className}`}>{badge.label}</span>
              {proDaysLeft != null && isPro && user.role !== "admin" && (
                <span className="text-sm text-muted-foreground">
                  {user.cancel_at_period_end ? "Access until" : "Renews on"} {formatDate(user.pro_until)} · <span className="font-medium text-foreground">{proDaysLeft} day{proDaysLeft === 1 ? "" : "s"} left</span>
                </span>
              )}
              {!isPro && (
                <span className="text-sm text-muted-foreground">Unlock voice OSCE, mock exams &amp; personal tutor.</span>
              )}
            </div>
          </div>
          <div className="flex gap-2 flex-wrap">
            {!isPro && (
              <Button onClick={() => navigate("/billing")} data-testid="upgrade-cta-btn" className="gap-2">
                <Sparkles className="w-4 h-4" /> Upgrade to Pro
              </Button>
            )}
            {isPro && user.role !== "admin" && (
              <Button variant="outline" onClick={() => navigate("/billing")} data-testid="change-plan-btn" className="gap-2">
                <BadgePercent className="w-4 h-4" /> Change plan / add time
              </Button>
            )}
            {isPro && user.role !== "admin" && !user.cancel_at_period_end && (
              <Button variant="ghost" onClick={onCancel} disabled={cancelling} data-testid="cancel-renew-btn" className="text-muted-foreground">
                {cancelling ? <Loader2 className="w-4 h-4 animate-spin" /> : "Stop auto-renew"}
              </Button>
            )}
            {isPro && user.role !== "admin" && user.cancel_at_period_end && user.has_stripe_subscription && (
              <Button variant="ghost" onClick={onResume} disabled={cancelling} data-testid="resume-renew-btn" className="text-primary">
                {cancelling ? <Loader2 className="w-4 h-4 animate-spin" /> : "Resume auto-renew"}
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Payment history */}
      <div className="rounded-2xl border border-border bg-card p-6 sm:p-8" data-testid="payment-history-card">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <h2 className="text-lg font-semibold">Payment history</h2>
            <p className="text-sm text-muted-foreground mb-4">All your past Pro purchases.</p>
          </div>
          <Button variant="outline" size="sm" onClick={syncFromStripe} disabled={syncing} className="rounded-full" data-testid="sync-stripe-btn">
            {syncing ? <Loader2 className="w-4 h-4 animate-spin" /> : "Paid but not showing? Refresh from Stripe"}
          </Button>
        </div>

        {loadingTxns ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground py-6">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading…
          </div>
        ) : paidTxns.length === 0 ? (
          <div className="text-sm text-muted-foreground py-8 text-center border border-dashed border-border rounded-xl">
            No payments yet. {!isPro && <button onClick={() => navigate("/billing")} className="text-primary underline ml-1">Try a free trial →</button>}
          </div>
        ) : (
          <div className="overflow-x-auto -mx-2">
            <table className="w-full text-sm">
              <thead className="text-xs uppercase text-muted-foreground border-b border-border">
                <tr>
                  <th className="text-left py-2 px-2">Date</th>
                  <th className="text-left py-2 px-2">Plan</th>
                  <th className="text-left py-2 px-2">Amount</th>
                  <th className="text-left py-2 px-2">Promo</th>
                  <th className="text-left py-2 px-2">Status</th>
                </tr>
              </thead>
              <tbody>
                {paidTxns.map((t) => (
                  <tr key={t.session_id} className="border-b border-border/50">
                    <td className="py-3 px-2 whitespace-nowrap">{formatDate(t.created_at)}</td>
                    <td className="py-3 px-2">{t.package_name}</td>
                    <td className="py-3 px-2 whitespace-nowrap">
                      {(t.currency || "usd").toUpperCase()} ${Number(t.amount || 0).toFixed(2)}
                      {t.original_amount && t.amount && t.original_amount > t.amount && (
                        <span className="text-xs text-muted-foreground line-through ml-2">${Number(t.original_amount).toFixed(2)}</span>
                      )}
                    </td>
                    <td className="py-3 px-2 text-muted-foreground">{t.promo_code || "-"}</td>
                    <td className="py-3 px-2">
                      <span className="inline-flex items-center gap-1 text-xs font-medium text-primary">
                        <CheckCircle2 className="w-3 h-3" /> Paid
                      </span>
                      {t.receipt_url && (
                        <a href={t.receipt_url} target="_blank" rel="noreferrer" className="ml-2 text-xs underline text-muted-foreground" data-testid={`receipt-${t.session_id}`}>Receipt</a>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

// ============== SECURITY TAB ==============
const SecurityTab = ({ logout }) => {
  const { user } = useAuth();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [saving, setSaving] = useState(false);
  const [signingOut, setSigningOut] = useState(false);
  const [events, setEvents] = useState([]);
  const [loadingEvents, setLoadingEvents] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [showDelete, setShowDelete] = useState(false);
  const [deletePw, setDeletePw] = useState("");
  const [deleting, setDeleting] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    axios.get(`${API}/auth/sessions/recent`)
      .then((r) => setEvents(r.data.events || []))
      .catch(() => {})
      .finally(() => setLoadingEvents(false));
  }, []);

  const onChangePassword = async (e) => {
    e.preventDefault();
    if (next.length < 6) {
      toast.error("New password must be at least 6 characters.");
      return;
    }
    if (next !== confirm) {
      toast.error("New passwords don't match.");
      return;
    }
    setSaving(true);
    try {
      await axios.post(`${API}/auth/change-password`, { current_password: current, new_password: next });
      setCurrent(""); setNext(""); setConfirm("");
      toast.success("Password changed. Please sign in again.");
      setTimeout(async () => { await logout(); navigate("/login"); }, 1200);
    } catch (err) {
      toast.error("Could not change password", { description: formatApiErrorDetail(err?.response?.data?.detail) });
    } finally {
      setSaving(false);
    }
  };

  const onSignOutOthers = async () => {
    setSigningOut(true);
    try {
      await axios.post(`${API}/auth/sign-out-others`);
      toast.success("All other devices signed out.");
    } catch (err) {
      toast.error("Could not sign out other devices", { description: formatApiErrorDetail(err?.response?.data?.detail) });
    } finally {
      setSigningOut(false);
    }
  };

  const onLogout = async () => {
    await logout();
    navigate("/login");
  };

  const onExportData = async () => {
    setExporting(true);
    try {
      const r = await axios.get(`${API}/me/export`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "passadc-my-data.json";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Your data export has been downloaded.");
    } catch (err) {
      toast.error("Could not export your data", { description: formatApiErrorDetail(err?.response?.data?.detail) });
    } finally {
      setExporting(false);
    }
  };

  const onDeleteAccount = async (e) => {
    e.preventDefault();
    if (!deletePw) return;
    setDeleting(true);
    try {
      await axios.post(`${API}/me/delete`, { password: deletePw });
      toast.success("Your account has been permanently deleted.");
      setTimeout(() => { window.location.href = "/"; }, 1200);
    } catch (err) {
      toast.error("Could not delete account", { description: formatApiErrorDetail(err?.response?.data?.detail) });
      setDeleting(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Change password */}
      <form onSubmit={onChangePassword} className="rounded-2xl border border-border bg-card p-6 sm:p-8 space-y-4" data-testid="change-password-form">
        <div>
          <h2 className="text-lg font-semibold">Change password</h2>
          <p className="text-sm text-muted-foreground">Your other devices will be signed out for security.</p>
        </div>
        <div className="space-y-2">
          <Label htmlFor="current">Current password</Label>
          <Input id="current" data-testid="input-current-pw" type="password" value={current} onChange={(e) => setCurrent(e.target.value)} required />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="new">New password</Label>
            <Input id="new" data-testid="input-new-pw" type="password" value={next} onChange={(e) => setNext(e.target.value)} required minLength={6} />
          </div>
          <div className="space-y-2">
            <Label htmlFor="confirm">Confirm new password</Label>
            <Input id="confirm" data-testid="input-confirm-pw" type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} required minLength={6} />
          </div>
        </div>
        <div className="flex justify-end">
          <Button type="submit" disabled={saving} data-testid="save-password-btn">
            {saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Updating…</> : "Update password"}
          </Button>
        </div>
      </form>

      {/* Session control */}
      <div className="rounded-2xl border border-border bg-card p-6 sm:p-8" data-testid="sessions-card">
        <h2 className="text-lg font-semibold">Active sessions</h2>
        <p className="text-sm text-muted-foreground">
          Only one device can be signed in at a time. Signing in from another browser will sign this one out.
        </p>
        <div className="flex flex-wrap gap-2 mt-4">
          <Button variant="outline" onClick={onSignOutOthers} disabled={signingOut} data-testid="signout-others-btn" className="gap-2">
            {signingOut ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldCheck className="w-4 h-4" />}
            Sign out other devices
          </Button>
          <Button variant="ghost" onClick={onLogout} data-testid="logout-btn" className="gap-2 text-muted-foreground">
            <LogOut className="w-4 h-4" /> Sign out this device
          </Button>
        </div>

        <Separator className="my-6" />

        <h3 className="text-sm font-semibold mb-2">Recent sign-ins</h3>
        {loadingEvents ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground py-4">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading…
          </div>
        ) : events.length === 0 ? (
          <p className="text-sm text-muted-foreground py-2">No recent sign-ins recorded.</p>
        ) : (
          <ul className="space-y-2 text-sm">
            {events.map((ev, i) => (
              <li key={i} className="flex items-start justify-between gap-3 py-2 border-b border-border/40 last:border-b-0">
                <div className="min-w-0">
                  <p className="font-medium truncate">{(ev.user_agent || "Unknown device").slice(0, 80)}</p>
                  <p className="text-xs text-muted-foreground">IP: {ev.ip || "unknown"}</p>
                </div>
                <span className="text-xs text-muted-foreground whitespace-nowrap">{formatDateTime(ev.at)}</span>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Privacy & data */}
      <div className="rounded-2xl border border-border bg-card p-6 sm:p-8" data-testid="privacy-data-card">
        <h2 className="text-lg font-semibold">Privacy &amp; data</h2>
        <p className="text-sm text-muted-foreground">
          You can download a copy of everything we hold about you, or permanently delete your account -
          exactly as promised in our <a href="/privacy" className="underline hover:text-foreground">Privacy Policy</a>.
        </p>

        <div className="flex flex-wrap gap-2 mt-4">
          <Button variant="outline" onClick={onExportData} disabled={exporting} data-testid="export-my-data-btn" className="gap-2">
            {exporting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
            Download my data
          </Button>
        </div>

        <Separator className="my-6" />

        <div className="rounded-xl border border-destructive/30 bg-destructive/5 p-4 sm:p-5">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-destructive flex-shrink-0 mt-0.5" />
            <div className="min-w-0 flex-1">
              <h3 className="text-sm font-semibold text-destructive">Delete my account</h3>
              <p className="text-xs text-muted-foreground mt-1">
                Permanently removes your account, study history, progress and personal data.
                Any active subscription is cancelled. This cannot be undone.
              </p>

              {user?.role === "admin" ? (
                <p className="text-xs text-muted-foreground mt-3">Admin accounts can't be deleted from here.</p>
              ) : !showDelete ? (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowDelete(true)}
                  data-testid="delete-account-btn"
                  className="mt-3 gap-2 border-destructive/40 text-destructive hover:bg-destructive/10"
                >
                  <Trash2 className="w-4 h-4" /> Delete my account
                </Button>
              ) : (
                <form onSubmit={onDeleteAccount} className="mt-4 space-y-3" data-testid="delete-account-form">
                  <div className="space-y-2">
                    <Label htmlFor="delete-pw" className="text-xs">
                      {user?.has_password === false ? "Type DELETE to confirm" : "Enter your password to confirm"}
                    </Label>
                    <Input
                      id="delete-pw"
                      data-testid="delete-account-password"
                      type={user?.has_password === false ? "text" : "password"}
                      value={deletePw}
                      onChange={(e) => setDeletePw(e.target.value)}
                      placeholder={user?.has_password === false ? "DELETE" : "Your password"}
                      required
                      className="max-w-xs"
                    />
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <Button
                      type="submit"
                      variant="destructive"
                      size="sm"
                      disabled={deleting || !deletePw}
                      data-testid="confirm-delete-account-btn"
                      className="gap-2"
                    >
                      {deleting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                      Permanently delete
                    </Button>
                    <Button type="button" variant="ghost" size="sm" onClick={() => { setShowDelete(false); setDeletePw(""); }} data-testid="cancel-delete-account-btn">
                      Cancel
                    </Button>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// ============== REFER & EARN TAB ==============
const ReferTab = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ friend_name: "", friend_email: "" });
  const [sending, setSending] = useState(false);
  const [copied, setCopied] = useState(false);

  const load = async () => {
    try {
      const r = await axios.get(`${API}/referrals/me`);
      setData(r.data);
    } catch (err) {
      toast.error("Could not load referrals", { description: formatApiErrorDetail(err?.response?.data?.detail) });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const onInvite = async (e) => {
    e.preventDefault();
    if (!form.friend_name.trim() || !form.friend_email.trim()) return;
    setSending(true);
    try {
      await axios.post(`${API}/referrals/invite`, {
        friend_name: form.friend_name.trim(),
        friend_email: form.friend_email.trim().toLowerCase(),
      });
      toast.success("Invite sent!", { description: `${form.friend_name} will get an email with your invite shortly.` });
      setForm({ friend_name: "", friend_email: "" });
      await load();
    } catch (err) {
      toast.error("Could not send invite", { description: formatApiErrorDetail(err?.response?.data?.detail) });
    } finally {
      setSending(false);
    }
  };

  const onCopy = async () => {
    if (!data?.share_link) return;
    try {
      await navigator.clipboard.writeText(data.share_link);
      setCopied(true);
      toast.success("Link copied to clipboard");
      setTimeout(() => setCopied(false), 2200);
    } catch {
      toast.error("Could not copy - long-press the link to copy manually");
    }
  };

  const onShareWhatsApp = () => {
    if (!data?.share_link) return;
    const text = encodeURIComponent(
      `Hey - I'm prepping for the ADC Part 1 exam on PassADC. Use my link and get a ${data.referee_trial_days}-day free Pro trial: ${data.share_link}`
    );
    window.open(`https://wa.me/?text=${text}`, "_blank");
  };

  const onShareEmail = () => {
    if (!data?.share_link) return;
    const subject = encodeURIComponent("Study with me for the ADC Part 1 exam");
    const body = encodeURIComponent(
      `Hi,\n\nI've been using PassADC to study for the ADC Part 1 exam - voice OSCE role-play, 1300+ MCQs, on-demand tutor. Use my link to get a ${data.referee_trial_days}-day free Pro trial:\n\n${data.share_link}\n\n`
    );
    window.location.href = `mailto:?subject=${subject}&body=${body}`;
  };

  if (loading || !data) {
    return (
      <div className="rounded-2xl border border-border bg-card p-8 flex items-center gap-2 text-muted-foreground text-sm" data-testid="refer-loading">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading…
      </div>
    );
  }

  const { stats, share_link, reward_per_referral_days, referee_trial_days, invites } = data;

  return (
    <div className="space-y-6">
      {/* Hero card */}
      <div className="rounded-2xl border border-primary/30 bg-gradient-to-br from-primary/10 via-accent/5 to-transparent p-6 sm:p-8" data-testid="refer-hero">
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 rounded-2xl bg-primary/15 text-primary flex items-center justify-center flex-shrink-0">
            <Gift className="w-6 h-6" />
          </div>
          <div className="flex-1 min-w-0">
            <h2 className="text-xl font-bold">Give {referee_trial_days} days, get {reward_per_referral_days} days</h2>
            <p className="text-sm text-muted-foreground mt-1">
              Invite a friend studying for the ADC Part 1. They get a <strong>{referee_trial_days}-day free Pro trial</strong> (double the standard 7).
              When they upgrade to Pro, <strong>you get {reward_per_referral_days} free days</strong> added to your subscription.
            </p>
          </div>
        </div>

        {/* Stats grid */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-6">
          <StatTile testId="stat-invited" label="Invited" value={stats.invited} />
          <StatTile testId="stat-signed-up" label="Signed up" value={stats.signed_up} />
          <StatTile testId="stat-paid" label="Paid" value={stats.paid} />
          <StatTile testId="stat-days-earned" label="Days earned" value={stats.days_earned} accent />
        </div>
      </div>

      {/* Share link */}
      <div className="rounded-2xl border border-border bg-card p-6 sm:p-8" data-testid="share-link-card">
        <h3 className="text-base font-semibold">Your unique invite link</h3>
        <p className="text-sm text-muted-foreground mb-3">Share it on WhatsApp, in a study group chat, anywhere.</p>
        <div className="flex flex-col sm:flex-row gap-2">
          <div className="flex-1 px-4 py-2.5 rounded-lg bg-secondary border border-border text-sm font-mono truncate" data-testid="share-link-display">
            {share_link}
          </div>
          <Button onClick={onCopy} variant="outline" data-testid="copy-link-btn" className="gap-2">
            {copied ? <CheckCircle2 className="w-4 h-4" /> : <CreditCard className="w-4 h-4 opacity-0" />}
            {copied ? "Copied" : "Copy link"}
          </Button>
        </div>
        <div className="flex flex-wrap gap-2 mt-3">
          <Button variant="ghost" size="sm" onClick={onShareWhatsApp} data-testid="share-whatsapp-btn">Share on WhatsApp</Button>
          <Button variant="ghost" size="sm" onClick={onShareEmail} data-testid="share-email-btn">Share by email</Button>
        </div>
      </div>

      {/* Direct invite form */}
      <form onSubmit={onInvite} className="rounded-2xl border border-border bg-card p-6 sm:p-8 space-y-4" data-testid="invite-form">
        <div>
          <h3 className="text-base font-semibold">Send a personal invite</h3>
          <p className="text-sm text-muted-foreground">We'll email them on your behalf and they'll see your name on the invite.</p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div className="space-y-2">
            <Label htmlFor="friend_name">Friend's name</Label>
            <Input
              id="friend_name"
              data-testid="invite-name-input"
              value={form.friend_name}
              onChange={(e) => setForm((f) => ({ ...f, friend_name: e.target.value }))}
              placeholder="e.g. Priya"
              required
              maxLength={80}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="friend_email">Friend's email</Label>
            <Input
              id="friend_email"
              data-testid="invite-email-input"
              type="email"
              value={form.friend_email}
              onChange={(e) => setForm((f) => ({ ...f, friend_email: e.target.value }))}
              placeholder="priya@example.com"
              required
            />
          </div>
        </div>
        <div className="flex justify-end">
          <Button type="submit" disabled={sending} data-testid="send-invite-btn" className="gap-2">
            {sending ? <><Loader2 className="w-4 h-4 animate-spin" />Sending…</> : <><Sparkles className="w-4 h-4" />Send invite</>}
          </Button>
        </div>
      </form>

      {/* Invites table */}
      <div className="rounded-2xl border border-border bg-card p-6 sm:p-8" data-testid="invites-list-card">
        <h3 className="text-base font-semibold">Your invites</h3>
        <p className="text-sm text-muted-foreground mb-4">Track how your friends are progressing.</p>
        {invites.length === 0 ? (
          <div className="text-sm text-muted-foreground py-8 text-center border border-dashed border-border rounded-xl">
            No invites yet. Send one above or share your link.
          </div>
        ) : (
          <div className="overflow-x-auto -mx-2">
            <table className="w-full text-sm">
              <thead className="text-xs uppercase text-muted-foreground border-b border-border">
                <tr>
                  <th className="text-left py-2 px-2">Friend</th>
                  <th className="text-left py-2 px-2">Status</th>
                  <th className="text-left py-2 px-2">Reward</th>
                  <th className="text-left py-2 px-2">Sent</th>
                </tr>
              </thead>
              <tbody>
                {invites.map((inv) => {
                  const pill = inv.status === "paid"
                    ? { label: "Paid · earned ✨", className: "bg-primary/15 text-primary" }
                    : inv.status === "signed_up"
                    ? { label: "Signed up", className: "bg-accent/15 text-accent" }
                    : { label: "Invited", className: "bg-muted text-muted-foreground" };
                  return (
                    <tr key={inv.id} className="border-b border-border/50">
                      <td className="py-3 px-2">
                        <p className="font-medium">{inv.friend_name}</p>
                        <p className="text-xs text-muted-foreground">{inv.friend_email}</p>
                      </td>
                      <td className="py-3 px-2">
                        <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${pill.className}`}>{pill.label}</span>
                      </td>
                      <td className="py-3 px-2 text-muted-foreground">
                        {inv.reward_granted ? `+${inv.reward_days || reward_per_referral_days} days` : "-"}
                      </td>
                      <td className="py-3 px-2 text-muted-foreground whitespace-nowrap">{formatDate(inv.created_at)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

const StatTile = ({ label, value, testId, accent }) => (
  <div data-testid={testId} className={`rounded-xl border ${accent ? "border-primary/30 bg-primary/5" : "border-border bg-secondary/50"} px-4 py-3`}>
    <p className="text-xs text-muted-foreground">{label}</p>
    <p className={`text-2xl font-bold mt-1 ${accent ? "text-primary" : "text-foreground"}`}>{value}</p>
  </div>
);

// ============== SUPPORT TAB ==============
const TICKET_STATUS_PILLS = {
  open: { label: "Open", className: "bg-primary/15 text-primary border-primary/30" },
  in_progress: { label: "In progress", className: "bg-warning/15 text-warning border-warning/30" },
  resolved: { label: "Resolved", className: "bg-primary/15 text-primary border-primary/30" },
  closed: { label: "Closed", className: "bg-muted text-muted-foreground border-border" },
};

const SupportTab = () => {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openId, setOpenId] = useState(null);
  const [composing, setComposing] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/support/tickets`);
      setTickets(r.data.tickets || []);
    } catch {
      toast.error("Could not load tickets");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  if (loading) {
    return (
      <div className="rounded-2xl border border-border bg-card p-8 flex items-center gap-2 text-muted-foreground text-sm">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading…
      </div>
    );
  }

  if (openId) {
    return <TicketDetail ticketId={openId} onBack={() => { setOpenId(null); load(); }} />;
  }

  if (composing) {
    return <NewTicketForm onCancel={() => setComposing(false)} onCreated={(id) => { setComposing(false); load(); setOpenId(id); }} />;
  }

  return (
    <div className="space-y-4" data-testid="support-tab">
      <div className="rounded-2xl border border-primary/30 bg-gradient-to-br from-primary/10 to-transparent p-6 sm:p-8">
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 rounded-2xl bg-primary/15 text-primary flex items-center justify-center flex-shrink-0">
            <LifeBuoy className="w-6 h-6" />
          </div>
          <div className="min-w-0 flex-1">
            <h2 className="text-xl font-bold">Need a hand?</h2>
            <p className="text-sm text-muted-foreground mt-1">
              Open a ticket and our team will reply within 1 business day. You can report a bug,
              flag a content issue, or share feedback.
            </p>
            <Button
              className="mt-4 gap-2"
              onClick={() => setComposing(true)}
              data-testid="new-ticket-button"
            >
              <Plus className="w-4 h-4" /> New ticket
            </Button>
          </div>
        </div>
      </div>

      <div className="rounded-2xl border border-border bg-card p-6 sm:p-8" data-testid="my-tickets-card">
        <h3 className="text-base font-semibold mb-4">My tickets</h3>
        {tickets.length === 0 ? (
          <div className="text-sm text-muted-foreground py-8 text-center border border-dashed border-border rounded-xl">
            You haven't opened any tickets yet. Click <strong>New ticket</strong> above to get started.
          </div>
        ) : (
          <div className="overflow-x-auto -mx-2">
            <table className="w-full text-sm">
              <thead className="text-xs uppercase text-muted-foreground border-b border-border">
                <tr>
                  <th className="text-left py-2 px-2">Subject</th>
                  <th className="text-left py-2 px-2">Category</th>
                  <th className="text-left py-2 px-2">Status</th>
                  <th className="text-left py-2 px-2">Updated</th>
                </tr>
              </thead>
              <tbody>
                {tickets.map((t) => {
                  const pill = TICKET_STATUS_PILLS[t.status] || TICKET_STATUS_PILLS.open;
                  return (
                    <tr key={t.id} className="border-b border-border/50 hover:bg-secondary/40 cursor-pointer" onClick={() => setOpenId(t.id)} data-testid={`student-ticket-row-${t.id}`}>
                      <td className="py-3 px-2">
                        {t.student_unread && <span className="inline-block w-2 h-2 rounded-full bg-primary mr-2" aria-label="New reply" />}
                        <span className="font-medium">{t.subject}</span>
                      </td>
                      <td className="py-3 px-2 text-muted-foreground capitalize">{t.category}</td>
                      <td className="py-3 px-2">
                        <span className={`text-xs font-semibold px-2 py-0.5 rounded-full border ${pill.className}`}>{pill.label}</span>
                      </td>
                      <td className="py-3 px-2 text-muted-foreground whitespace-nowrap">{formatDate(t.updated_at)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

const CATEGORY_OPTIONS = [
  { value: "bug", label: "Bug / something broken" },
  { value: "content", label: "Content issue (question, lesson, etc.)" },
  { value: "feedback", label: "Feedback / suggestion" },
];

const NewTicketForm = ({ onCancel, onCreated }) => {
  const [category, setCategory] = useState("bug");
  const [subject, setSubject] = useState("");
  const [description, setDescription] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const submit = async () => {
    if (subject.trim().length < 3) {
      toast.error("Please add a subject (at least 3 characters).");
      return;
    }
    if (description.trim().length < 10) {
      toast.error("Please describe your issue (at least 10 characters).");
      return;
    }
    setSubmitting(true);
    try {
      const r = await axios.post(`${API}/support/tickets`, {
        category,
        subject: subject.trim(),
        description: description.trim(),
        page_url: window.location.href,
      });
      toast.success("Ticket submitted - we'll reply soon!");
      onCreated(r.data?.ticket?.id);
    } catch (e) {
      toast.error(formatApiErrorDetail(e) || "Could not submit ticket");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="new-ticket-form">
      <button
        onClick={onCancel}
        className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"
        data-testid="new-ticket-back"
      >
        <ArrowLeft className="w-4 h-4" /> Back to tickets
      </button>

      <div className="rounded-2xl border border-border bg-card p-6 sm:p-8 space-y-5">
        <div>
          <h2 className="text-xl font-bold">Open a ticket</h2>
          <p className="text-sm text-muted-foreground mt-1">Tell us what's going on and we'll help.</p>
        </div>

        <div className="space-y-2">
          <Label htmlFor="ticket-category">Category</Label>
          <select
            id="ticket-category"
            data-testid="ticket-category-select"
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
          >
            {CATEGORY_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </div>

        <div className="space-y-2">
          <Label htmlFor="ticket-subject">Subject</Label>
          <Input
            id="ticket-subject"
            data-testid="ticket-subject-input"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            placeholder="e.g. Answer key looks wrong on an endodontics question"
            maxLength={160}
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="ticket-description">Description</Label>
          <textarea
            id="ticket-description"
            data-testid="ticket-description-input"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Describe what happened, what you expected, and any steps to reproduce it."
            rows={6}
            maxLength={4000}
            className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 resize-y"
          />
        </div>

        <div className="flex items-center gap-3">
          <Button onClick={submit} disabled={submitting} className="gap-2" data-testid="ticket-submit-button">
            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
            {submitting ? "Submitting…" : "Submit ticket"}
          </Button>
          <Button variant="ghost" onClick={onCancel} disabled={submitting} data-testid="ticket-cancel-button">
            Cancel
          </Button>
        </div>
      </div>
    </div>
  );
};

const TicketDetail = ({ ticketId, onBack }) => {
  const [ticket, setTicket] = useState(null);
  const [loading, setLoading] = useState(true);
  const [reply, setReply] = useState("");
  const [sending, setSending] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/support/tickets/${ticketId}`);
      setTicket(r.data);
    } catch (err) {
      toast.error("Could not load ticket", { description: formatApiErrorDetail(err?.response?.data?.detail) });
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [ticketId]);

  const onSend = async (e) => {
    e.preventDefault();
    if (!reply.trim()) return;
    setSending(true);
    try {
      await axios.post(`${API}/support/tickets/${ticketId}/messages`, { body: reply.trim() });
      setReply("");
      await load();
    } catch (err) {
      toast.error("Could not send", { description: formatApiErrorDetail(err?.response?.data?.detail) });
    } finally {
      setSending(false);
    }
  };

  if (loading || !ticket) {
    return <div className="flex items-center gap-2 text-muted-foreground text-sm"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>;
  }

  const pill = TICKET_STATUS_PILLS[ticket.status] || TICKET_STATUS_PILLS.open;
  const closed = ticket.status === "closed";

  return (
    <div className="space-y-4" data-testid="student-ticket-detail">
      <button onClick={onBack} className="text-sm text-muted-foreground hover:text-foreground inline-flex items-center gap-1">← Back to tickets</button>
      <div className="rounded-2xl border border-border bg-card p-6">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="min-w-0">
            <p className="text-xs uppercase tracking-wider text-muted-foreground">{ticket.category}</p>
            <h2 className="text-xl font-bold tracking-tight mt-1">{ticket.subject}</h2>
          </div>
          <span className={`text-xs font-semibold px-2 py-0.5 rounded-full border ${pill.className}`}>{pill.label}</span>
        </div>
      </div>

      <div className="space-y-3">
        {ticket.messages?.map((m) => (
          <div key={m.id} className={`rounded-xl border p-4 ${m.author_role === "admin" ? "border-primary/30 bg-primary/5" : "border-border bg-card ml-8"}`}>
            <div className="flex items-baseline justify-between gap-3 mb-2">
              <p className="text-sm font-semibold">{m.author_name} <span className="text-xs text-muted-foreground font-normal ml-1">({m.author_role === "admin" ? "PassADC team" : "you"})</span></p>
              <p className="text-xs text-muted-foreground">{formatDateTime(m.created_at)}</p>
            </div>
            <p className="text-sm whitespace-pre-wrap">{m.body}</p>
          </div>
        ))}
      </div>

      {closed ? (
        <div className="rounded-xl border border-dashed border-border p-4 text-sm text-muted-foreground">
          This ticket is closed. Open a new ticket via the <strong>?</strong> button if you need more help.
        </div>
      ) : (
        <form onSubmit={onSend} className="rounded-2xl border border-border bg-card p-4 space-y-3" data-testid="student-reply-form">
          <textarea
            value={reply}
            onChange={(e) => setReply(e.target.value)}
            rows={3}
            maxLength={4000}
            placeholder="Reply to PassADC support…"
            data-testid="student-reply-input"
            className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm resize-y"
          />
          <div className="flex justify-end">
            <Button type="submit" disabled={sending || !reply.trim()} data-testid="student-reply-send">
              {sending ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Sending…</> : "Send reply"}
            </Button>
          </div>
        </form>
      )}
    </div>
  );
};

// ============== NOTIFICATIONS TAB ==============
const NotificationsTab = () => {
  const [prefs, setPrefs] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    axios.get(`${API}/auth/preferences`)
      .then((r) => setPrefs(r.data))
      .catch(() => setPrefs({ daily_briefing_email: false, new_device_alert_email: true, weekly_progress_email: false }))
      .finally(() => setLoading(false));
  }, []);

  const update = async (key, value) => {
    setSaving(true);
    setPrefs((p) => ({ ...p, [key]: value }));
    try {
      const r = await axios.patch(`${API}/auth/preferences`, { [key]: value });
      setPrefs(r.data);
    } catch (err) {
      toast.error("Could not save preference", { description: formatApiErrorDetail(err?.response?.data?.detail) });
    } finally {
      setSaving(false);
    }
  };

  if (loading || !prefs) {
    return (
      <div className="rounded-2xl border border-border bg-card p-8 flex items-center gap-2 text-muted-foreground text-sm">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading…
      </div>
    );
  }

  const Row = ({ label, hint, k, testId }) => (
    <div className="flex items-start justify-between gap-4 py-4">
      <div className="min-w-0">
        <p className="text-sm font-medium">{label}</p>
        {hint && <p className="text-xs text-muted-foreground mt-0.5">{hint}</p>}
      </div>
      <Switch checked={!!prefs[k]} onCheckedChange={(v) => update(k, v)} disabled={saving} data-testid={testId} />
    </div>
  );

  return (
    <div className="rounded-2xl border border-border bg-card p-6 sm:p-8" data-testid="notifications-card">
      <h2 className="text-lg font-semibold">Email notifications</h2>
      <p className="text-sm text-muted-foreground mb-2">
        Email delivery is coming soon - these settings will apply automatically once it's live.
      </p>
      <Separator className="my-2" />
      <Row
        label="Daily study briefing"
        hint="A short morning email with your focus subject for the day."
        k="daily_briefing_email"
        testId="toggle-daily-briefing"
      />
      <Separator />
      <Row
        label="New-device sign-in alerts"
        hint="Get notified when your account is accessed from a new device."
        k="new_device_alert_email"
        testId="toggle-new-device-alert"
      />
      <Separator />
      <Row
        label="Weekly progress digest"
        hint="A Sunday recap of your accuracy, streak and readiness score."
        k="weekly_progress_email"
        testId="toggle-weekly-progress"
      />
    </div>
  );
};

export default AccountPage;
